// Epic Integration Admin Settings Page
// Admin-only configuration for Epic FHIR pushback

import { useState } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Activity,
  Building2,
  CheckCircle2,
  Edit,
  FileText,
  Inbox,
  ListTodo,
  Plus,
  Save,
  Server,
  Settings,
  Shield,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  useEpicSettings, 
  useUpdateEpicSettings,
  useEpicTemplates,
  useUpdateEpicTemplate,
} from '@/hooks/useEpicPushback';
import {
  EpicIntegrationSettings,
  EpicPushbackTemplate,
  EpicPushbackEventType,
  EVENT_TYPE_LABELS,
  DESTINATION_LABELS,
  TEMPLATE_PLACEHOLDERS,
} from '@/lib/epic/pushbackTypes';

export default function EpicIntegrationSettingsPage() {
  const [selectedClientId, setSelectedClientId] = useState<string>('default');
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EpicPushbackTemplate | null>(null);
  
  const { data: allSettings, isLoading: loadingSettings } = useEpicSettings();
  const { data: templates, isLoading: loadingTemplates } = useEpicTemplates(selectedClientId);
  const updateSettings = useUpdateEpicSettings();
  const updateTemplate = useUpdateEpicTemplate();
  
  // Get or create default settings for selected client
  const currentSettings: EpicIntegrationSettings = allSettings?.find(s => s.client_id === selectedClientId) || {
    id: '',
    client_id: selectedClientId,
    client_name: selectedClientId === 'default' ? 'Default Settings' : selectedClientId,
    environment: 'sandbox' as const,
    epic_enabled: false,
    fhir_base_url: '',
    oauth_client_id: '',
    oauth_redirect_uri: '',
    pushback_enabled: false,
    pushback_inbasket: true,
    pushback_task: true,
    pushback_chart_note: false,
    default_pushback_destination: 'task' as const,
    pushback_on_case_created: false,
    pushback_on_submitted: true,
    pushback_on_more_info: true,
    pushback_on_approved: true,
    pushback_on_denied: true,
    pushback_on_appealed: true,
    pushback_on_closed: false,
    created_at: '',
    updated_at: '',
  };
  
  const handleSaveSettings = async (updates: Partial<EpicIntegrationSettings>) => {
    await updateSettings.mutateAsync({
      ...currentSettings,
      ...updates,
      client_id: selectedClientId,
      client_name: currentSettings.client_name || selectedClientId,
    });
  };
  
  const handleEditTemplate = (template: EpicPushbackTemplate) => {
    setEditingTemplate(template);
    setShowTemplateDialog(true);
  };
  
  const handleSaveTemplate = async (template: Partial<EpicPushbackTemplate>) => {
    if (!editingTemplate) return;
    
    await updateTemplate.mutateAsync({
      client_id: selectedClientId,
      event_type: editingTemplate.event_type,
      template_name: template.template_name || editingTemplate.template_name,
      subject_template: template.subject_template || editingTemplate.subject_template,
      body_template: template.body_template || editingTemplate.body_template,
    });
    
    setShowTemplateDialog(false);
    setEditingTemplate(null);
  };
  
  if (loadingSettings) {
    return (
      <OpsLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-muted-foreground">Loading Epic settings...</div>
        </div>
      </OpsLayout>
    );
  }
  
  return (
    <OpsLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Epic Integration Settings</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Configure Epic FHIR pushback for authorization status updates
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default Settings</SelectItem>
                {allSettings?.filter(s => s.client_id !== 'default').map(s => (
                  <SelectItem key={s.client_id} value={s.client_id}>
                    {s.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setShowAddClientDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="connection" className="space-y-6">
          <TabsList>
            <TabsTrigger value="connection">Connection</TabsTrigger>
            <TabsTrigger value="pushback">Pushback Events</TabsTrigger>
            <TabsTrigger value="templates">Message Templates</TabsTrigger>
          </TabsList>
          
          {/* Connection Settings */}
          <TabsContent value="connection" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Environment Settings */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Server className="w-5 h-5 text-purple-500" />
                    <div>
                      <CardTitle className="text-lg">Environment</CardTitle>
                      <CardDescription>Epic FHIR connection settings</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Epic Integration Enabled</Label>
                      <p className="text-xs text-muted-foreground">
                        Enable SMART-on-FHIR connection to Epic
                      </p>
                    </div>
                    <Switch
                      checked={currentSettings.epic_enabled}
                      onCheckedChange={(checked) => handleSaveSettings({ epic_enabled: checked })}
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <Label>Environment</Label>
                    <Select 
                      value={currentSettings.environment}
                      onValueChange={(v) => handleSaveSettings({ environment: v as 'sandbox' | 'production' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                              Sandbox
                            </Badge>
                            <span>Development/Testing</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="production">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-emerald-500 border-emerald-500/30">
                              Production
                            </Badge>
                            <span>Live Environment</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
              
              {/* OAuth Settings */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <div>
                      <CardTitle className="text-lg">OAuth / SMART Settings</CardTitle>
                      <CardDescription>SMART-on-FHIR client configuration</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>FHIR Base URL</Label>
                    <Input 
                      placeholder="https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4"
                      value={currentSettings.fhir_base_url || ''}
                      onChange={(e) => handleSaveSettings({ fhir_base_url: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>OAuth Client ID</Label>
                    <Input 
                      placeholder="your-client-id"
                      value={currentSettings.oauth_client_id || ''}
                      onChange={(e) => handleSaveSettings({ oauth_client_id: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Redirect URI</Label>
                    <Input 
                      placeholder="https://your-app.com/launch"
                      value={currentSettings.oauth_redirect_uri || ''}
                      onChange={(e) => handleSaveSettings({ oauth_redirect_uri: e.target.value })}
                    />
                  </div>
                  
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">
                      <strong>Note:</strong> These are placeholder settings. Full OAuth integration requires 
                      registering with Epic and configuring PKCE authentication flow.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Pushback Events */}
          <TabsContent value="pushback" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-amber-500" />
                    <div>
                      <CardTitle className="text-lg">Pushback Configuration</CardTitle>
                      <CardDescription>Configure which events trigger Epic notifications</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">Enable Pushback</Label>
                    <Switch
                      checked={currentSettings.pushback_enabled}
                      onCheckedChange={(checked) => handleSaveSettings({ pushback_enabled: checked })}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Destinations */}
                <div>
                  <h4 className="font-medium mb-3">Pushback Destinations</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className={cn(
                      "p-4 rounded-lg border transition-colors cursor-pointer",
                      currentSettings.pushback_inbasket 
                        ? "border-blue-500/50 bg-blue-500/10" 
                        : "border-border hover:border-muted-foreground"
                    )}
                    onClick={() => handleSaveSettings({ pushback_inbasket: !currentSettings.pushback_inbasket })}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Inbox className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">InBasket</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Send messages to provider's Epic InBasket
                      </p>
                      <div className="mt-2">
                        <Switch checked={currentSettings.pushback_inbasket} />
                      </div>
                    </div>
                    
                    <div className={cn(
                      "p-4 rounded-lg border transition-colors cursor-pointer",
                      currentSettings.pushback_task 
                        ? "border-emerald-500/50 bg-emerald-500/10" 
                        : "border-border hover:border-muted-foreground"
                    )}
                    onClick={() => handleSaveSettings({ pushback_task: !currentSettings.pushback_task })}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <ListTodo className="w-5 h-5 text-emerald-500" />
                        <span className="font-medium">Task</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Create tasks in provider's task list
                      </p>
                      <div className="mt-2">
                        <Switch checked={currentSettings.pushback_task} />
                      </div>
                    </div>
                    
                    <div className={cn(
                      "p-4 rounded-lg border transition-colors cursor-pointer",
                      currentSettings.pushback_chart_note 
                        ? "border-purple-500/50 bg-purple-500/10" 
                        : "border-border hover:border-muted-foreground"
                    )}
                    onClick={() => handleSaveSettings({ pushback_chart_note: !currentSettings.pushback_chart_note })}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-purple-500" />
                        <span className="font-medium">Chart Note</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Add notes to patient's chart
                      </p>
                      <div className="mt-2">
                        <Switch checked={currentSettings.pushback_chart_note} />
                      </div>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                {/* Default Destination */}
                <div className="space-y-2">
                  <Label>Default Pushback Destination</Label>
                  <Select 
                    value={currentSettings.default_pushback_destination}
                    onValueChange={(v) => handleSaveSettings({ default_pushback_destination: v as 'inbasket' | 'task' | 'chart_note' })}
                  >
                    <SelectTrigger className="w-[300px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inbasket">{DESTINATION_LABELS.inbasket}</SelectItem>
                      <SelectItem value="task">{DESTINATION_LABELS.task}</SelectItem>
                      <SelectItem value="chart_note">{DESTINATION_LABELS.chart_note}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Separator />
                
                {/* Event Toggles */}
                <div>
                  <h4 className="font-medium mb-3">Trigger Events</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {([
                      { key: 'pushback_on_case_created', event: 'case_created', optional: true },
                      { key: 'pushback_on_submitted', event: 'submitted', optional: false },
                      { key: 'pushback_on_more_info', event: 'more_info', optional: false },
                      { key: 'pushback_on_approved', event: 'approved', optional: false },
                      { key: 'pushback_on_denied', event: 'denied', optional: false },
                      { key: 'pushback_on_appealed', event: 'appealed', optional: false },
                      { key: 'pushback_on_closed', event: 'closed', optional: true },
                    ] as const).map(({ key, event, optional }) => (
                      <div 
                        key={key}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <Activity className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <span className="text-sm font-medium">
                              {EVENT_TYPE_LABELS[event]}
                            </span>
                            {optional && (
                              <Badge variant="outline" className="ml-2 text-[10px]">Optional</Badge>
                            )}
                          </div>
                        </div>
                        <Switch
                          checked={currentSettings[key]}
                          onCheckedChange={(checked) => handleSaveSettings({ [key]: checked })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Message Templates */}
          <TabsContent value="templates" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-cyan-500" />
                  <div>
                    <CardTitle className="text-lg">Message Templates</CardTitle>
                    <CardDescription>
                      Customize pushback messages per event type. Use placeholders for dynamic content.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingTemplates ? (
                  <div className="animate-pulse text-muted-foreground">Loading templates...</div>
                ) : (
                  <div className="space-y-3">
                    {Object.keys(EVENT_TYPE_LABELS).map((eventType) => {
                      const template = templates?.find(t => 
                        t.event_type === eventType && 
                        (t.client_id === selectedClientId || t.client_id === 'default')
                      );
                      
                      return (
                        <div 
                          key={eventType}
                          className="flex items-center justify-between p-4 rounded-lg border hover:border-muted-foreground/50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                              <Activity className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                              <h4 className="font-medium">{EVENT_TYPE_LABELS[eventType as EpicPushbackEventType]}</h4>
                              <p className="text-sm text-muted-foreground">
                                {template?.template_name || 'Using default template'}
                              </p>
                              {template && (
                                <Badge variant="outline" className="mt-1 text-[10px]">
                                  v{template.version}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => template && handleEditTemplate(template)}
                            disabled={!template}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Placeholder Reference */}
                <div className="mt-6 p-4 rounded-lg bg-muted/50">
                  <h4 className="font-medium mb-2">Available Placeholders</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    {Object.entries(TEMPLATE_PLACEHOLDERS).map(([key, desc]) => (
                      <div key={key} className="flex items-center gap-2">
                        <code className="px-1 py-0.5 rounded bg-muted text-primary">
                          {`{{${key}}}`}
                        </code>
                        <span className="text-muted-foreground truncate">{desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Template Edit Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Edit Template: {editingTemplate && EVENT_TYPE_LABELS[editingTemplate.event_type]}
              </DialogTitle>
              <DialogDescription>
                Customize the message sent to Epic for this event type
              </DialogDescription>
            </DialogHeader>
            
            {editingTemplate && (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input 
                    value={editingTemplate.template_name}
                    onChange={(e) => setEditingTemplate({
                      ...editingTemplate,
                      template_name: e.target.value,
                    })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Subject Line</Label>
                  <Input 
                    value={editingTemplate.subject_template}
                    onChange={(e) => setEditingTemplate({
                      ...editingTemplate,
                      subject_template: e.target.value,
                    })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Message Body</Label>
                  <Textarea 
                    className="min-h-[200px] font-mono text-sm"
                    value={editingTemplate.body_template}
                    onChange={(e) => setEditingTemplate({
                      ...editingTemplate,
                      body_template: e.target.value,
                    })}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={() => editingTemplate && handleSaveTemplate(editingTemplate)}>
                <Save className="w-4 h-4 mr-2" />
                Save Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Add Client Dialog */}
        <AddClientDialog 
          open={showAddClientDialog} 
          onOpenChange={setShowAddClientDialog}
          onSave={(clientId, clientName) => {
            handleSaveSettings({ 
              client_id: clientId, 
              client_name: clientName,
            });
            setSelectedClientId(clientId);
            setShowAddClientDialog(false);
          }}
        />
      </div>
    </OpsLayout>
  );
}

function AddClientDialog({ 
  open, 
  onOpenChange, 
  onSave 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  onSave: (clientId: string, clientName: string) => void;
}) {
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  
  const handleSave = () => {
    if (!clientId || !clientName) {
      toast.error('Please fill in all fields');
      return;
    }
    onSave(clientId.toLowerCase().replace(/\s+/g, '-'), clientName);
    setClientId('');
    setClientName('');
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Client</DialogTitle>
          <DialogDescription>
            Create Epic integration settings for a new hospital or practice
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Client ID</Label>
            <Input 
              placeholder="e.g., mercy-hospital"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Unique identifier (lowercase, no spaces)
            </p>
          </div>
          
          <div className="space-y-2">
            <Label>Client Name</Label>
            <Input 
              placeholder="e.g., Mercy Hospital"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
